import { useState, useEffect, useCallback } from 'react';
import { DecisionTreeQuestion } from '../../../types';
import { QuestionState, UserStepAnswer } from '../utils/types';
import { FEEDBACK_DELAY_MS } from '../utils/constants';
import {
  createUserStepAnswer,
  isAnswerCorrect,
  findDecisionPath,
  findStepIndexById,
  isExplicitFinalization,
} from '../utils/utils';

interface UseDecisionTreeStateProps {
  question: DecisionTreeQuestion;
  isTest: boolean;
  resolved?: boolean;
  userAnswersAnswered?: UserStepAnswer[];
  setUserAnswersAnswered?: (answers: UserStepAnswer[]) => void;
  handleSubmit?: () => void;
  allowMultipleOnlyFlowWhenNoSingle?: boolean;
  requireFinalSubmitClick?: boolean;
}

const findLastSingleStepIndex = (answers: UserStepAnswer[]): number => {
  const singleAnswers = answers.filter((a) => a.stepType === 'single');
  if (singleAnswers.length === 0) return -1;

  return singleAnswers[singleAnswers.length - 1].stepIndex;
};

const findLastMultipleStepAnswer = (answers: UserStepAnswer[]): UserStepAnswer | undefined => {
  for (let i = answers.length - 1; i >= 0; i--) {
    if (answers[i].stepType === 'multiple') {
      return answers[i];
    }
  }

  return undefined;
};

const buildInitialState = ({
  question,
  resolved,
  userAnswersAnswered,
  isTest,
  allowMultipleOnlyFlowWhenNoSingle,
}: Pick<
  UseDecisionTreeStateProps,
  'question' | 'resolved' | 'userAnswersAnswered' | 'isTest' | 'allowMultipleOnlyFlowWhenNoSingle'
>): QuestionState => {
  if (resolved && userAnswersAnswered && userAnswersAnswered.length > 0) {
    const lastSingleStepIndex = userAnswersAnswered.length - 1;
    let reviewStartIndex = lastSingleStepIndex;

    for (let i = userAnswersAnswered.length - 1; i >= 0; i--) {
      if (userAnswersAnswered[i].stepType === 'single') {
        reviewStartIndex = i;
        break;
      }
    }

    return {
      phase: 'results',
      currentMultipleIndex: 0,
      currentSingleIndex: 0,
      selectedOption: null,
      userAnswers: userAnswersAnswered,
      reviewIndex: reviewStartIndex,
      finished: false,
      waitingFinalSubmit: false,
    };
  }

  if (isTest && userAnswersAnswered && userAnswersAnswered.length > 0) {
    const lastSingleIndex = findLastSingleStepIndex(userAnswersAnswered);

    if (lastSingleIndex >= 0) {
      const lastSingleAnswer = userAnswersAnswered.find(
        (a) => a.stepType === 'single' && a.stepIndex === lastSingleIndex,
      );

      return {
        phase: 'single',
        currentMultipleIndex: 0,
        currentSingleIndex: lastSingleIndex,
        selectedOption: lastSingleAnswer?.selectedAnswerId || null,
        userAnswers: userAnswersAnswered,
        reviewIndex: 0,
        finished: false,
        waitingFinalSubmit: false,
      };
    }

    const hasNoSingleSteps = (question.step_single?.length || 0) === 0;
    if (allowMultipleOnlyFlowWhenNoSingle && hasNoSingleSteps) {
      const lastMultipleAnswer = findLastMultipleStepAnswer(userAnswersAnswered);

      if (lastMultipleAnswer) {
        return {
          phase: 'multiple',
          currentMultipleIndex: lastMultipleAnswer.stepIndex,
          currentSingleIndex: 0,
          selectedOption: lastMultipleAnswer.selectedAnswerId,
          userAnswers: userAnswersAnswered,
          reviewIndex: 0,
          finished: false,
          waitingFinalSubmit: false,
        };
      }
    }
  }

  return {
    phase: 'multiple',
    currentMultipleIndex: 0,
    currentSingleIndex: 0,
    selectedOption: null,
    userAnswers: userAnswersAnswered || [],
    reviewIndex: 0,
    finished: false,
    waitingFinalSubmit: false,
  };
};

export const useDecisionTreeState = ({
  question,
  isTest,
  resolved,
  userAnswersAnswered,
  setUserAnswersAnswered,
  handleSubmit,
  allowMultipleOnlyFlowWhenNoSingle = false,
  requireFinalSubmitClick = false,
}: UseDecisionTreeStateProps) => {
  const [state, setState] = useState<QuestionState>(() =>
    buildInitialState({
      question,
      resolved,
      userAnswersAnswered,
      isTest,
      allowMultipleOnlyFlowWhenNoSingle,
    }),
  );

  const totalMultipleSteps = question.step_multiple?.length || 0;
  const totalSingleSteps = question.step_single?.length || 0;

  const currentStep =
    state.phase === 'multiple'
      ? question.step_multiple?.[state.currentMultipleIndex]
      : state.phase === 'single'
        ? question.step_single?.[state.currentSingleIndex]
        : null;

  useEffect(() => {
    setState(
      buildInitialState({
        question,
        resolved,
        userAnswersAnswered,
        isTest,
        allowMultipleOnlyFlowWhenNoSingle,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id_questao]);

  const handleMultipleOptionClick = useCallback(
    (optionId: number) => {
      if (!currentStep) return;

      if (
        isTest &&
        userAnswersAnswered &&
        userAnswersAnswered.length > 0 &&
        state.phase === 'single'
      ) {
        return;
      }

      setState((prev) => ({ ...prev, selectedOption: optionId, waitingFinalSubmit: false }));

      const correct = isAnswerCorrect(currentStep.answers, optionId);
      const newAnswer = createUserStepAnswer(
        state.currentMultipleIndex,
        'multiple',
        currentStep.statement || '',
        optionId,
        correct,
        currentStep.answers || [],
      );

      const existingAnswerIndex = state.userAnswers.findIndex(
        (a) => a.stepType === 'multiple' && a.stepIndex === state.currentMultipleIndex,
      );

      let updatedAnswers: UserStepAnswer[];
      if (existingAnswerIndex >= 0) {
        updatedAnswers = [...state.userAnswers];
        updatedAnswers[existingAnswerIndex] = newAnswer;
      } else {
        updatedAnswers = [...state.userAnswers, newAnswer];
      }

      setState((prev) => ({ ...prev, userAnswers: updatedAnswers }));
      setUserAnswersAnswered?.(updatedAnswers);

      setTimeout(() => {
        const decisionPath = findDecisionPath(
          question.decision_tree || [],
          currentStep.id,
          optionId,
        );
        const nextStepId = decisionPath?.target_step_id ?? null;
        const isExplicitFinalizationPath = isExplicitFinalization(decisionPath);

        const finishMultipleOnlyFlow = () => {
          if (requireFinalSubmitClick) {
            setState((prev) => ({
              ...prev,
              waitingFinalSubmit: true,
            }));
            return;
          }

          if (isTest && handleSubmit) {
            handleSubmit();
            return;
          }

          setState((prev) => ({
            ...prev,
            selectedOption: null,
            phase: 'results',
            finished: true,
            reviewIndex: Math.max(updatedAnswers.length - 1, 0),
            waitingFinalSubmit: false,
          }));
        };

        const shouldUseMultipleOnlyFlow =
          allowMultipleOnlyFlowWhenNoSingle && totalSingleSteps === 0;
        
        if (isExplicitFinalizationPath) {
          finishMultipleOnlyFlow();
          return;
        }

        if (nextStepId !== null) {
          const nextStepIndex = findStepIndexById(question.step_multiple || [], nextStepId);

          if (nextStepIndex !== -1) {
            setState((prev) => ({
              ...prev,
              selectedOption: null,
              currentMultipleIndex: nextStepIndex,
              waitingFinalSubmit: false,
            }));
          } else {
            if (shouldUseMultipleOnlyFlow) {
              finishMultipleOnlyFlow();
              return;
            }

            setState((prev) => ({
              ...prev,
              selectedOption: null,
              phase: 'single',
              currentSingleIndex: 0,
              waitingFinalSubmit: false,
            }));
          }
        } else {
          const hasNextMultipleStep = state.currentMultipleIndex < totalMultipleSteps - 1;

          if (hasNextMultipleStep) {
            setState((prev) => ({
              ...prev,
              selectedOption: null,
              currentMultipleIndex: prev.currentMultipleIndex + 1,
              waitingFinalSubmit: false,
            }));
            return;
          }

          if (shouldUseMultipleOnlyFlow) {
            finishMultipleOnlyFlow();
            return;
          }

          setState((prev) => ({
            ...prev,
            selectedOption: null,
            phase: 'single',
            currentSingleIndex: 0,
            waitingFinalSubmit: false,
          }));
        }
      }, FEEDBACK_DELAY_MS);
    },
    // eslint-disable-next-line
    [
      currentStep,
      state.currentMultipleIndex,
      state.userAnswers,
      totalMultipleSteps,
      totalSingleSteps,
      question.decision_tree,
      question.step_multiple,
      setUserAnswersAnswered,
      isTest,
      userAnswersAnswered,
      handleSubmit,
      allowMultipleOnlyFlowWhenNoSingle,
      requireFinalSubmitClick,
    ],
  );

  const handleSingleOptionClick = useCallback(
    (optionId: number) => {
      if (!currentStep) return;

      setState((prev) => ({ ...prev, selectedOption: optionId, waitingFinalSubmit: false }));

      const correct = isAnswerCorrect(currentStep.answers, optionId);
      const newAnswer = createUserStepAnswer(
        state.currentSingleIndex,
        'single',
        currentStep.statement || '',
        optionId,
        correct,
        currentStep.answers || [],
      );

      const existingAnswerIndex = state.userAnswers.findIndex(
        (a) => a.stepType === 'single' && a.stepIndex === state.currentSingleIndex,
      );

      let updatedAnswers: UserStepAnswer[];
      if (existingAnswerIndex >= 0) {
        updatedAnswers = [...state.userAnswers];
        updatedAnswers[existingAnswerIndex] = newAnswer;
      } else {
        updatedAnswers = [...state.userAnswers, newAnswer];
      }

      setState((prev) => ({ ...prev, userAnswers: updatedAnswers }));
      setUserAnswersAnswered?.(updatedAnswers);

      const isLastSingleStep = state.currentSingleIndex === totalSingleSteps - 1;

      setTimeout(() => {
        if (isLastSingleStep) {
          if (requireFinalSubmitClick) {
            setState((prev) => {
              if (prev.phase === 'results' || prev.finished) {
                return prev;
              }

              return {
                ...prev,
                waitingFinalSubmit: true,
              };
            });
            return;
          }

          if (isTest && handleSubmit) {
            handleSubmit();
          } else {
            setState((prev) => ({
              ...prev,
              phase: 'results',
              finished: true,
              selectedOption: null,
              reviewIndex: totalMultipleSteps + totalSingleSteps - 1,
              waitingFinalSubmit: false,
            }));
          }
        } else {
          setState((prev) => ({
            ...prev,
            currentSingleIndex: prev.currentSingleIndex + 1,
            selectedOption: null,
            waitingFinalSubmit: false,
          }));
        }
      }, FEEDBACK_DELAY_MS);
    },
    [
      currentStep,
      state.currentSingleIndex,
      state.userAnswers,
      isTest,
      setUserAnswersAnswered,
      totalSingleSteps,
      totalMultipleSteps,
      handleSubmit,
      requireFinalSubmitClick,
    ],
  );

  const handleSingleSubmit = useCallback(() => {
    const isLastSingleStep =
      totalSingleSteps > 0 && state.currentSingleIndex === totalSingleSteps - 1;
    const shouldFinalizeFromFinalSubmitClick =
      requireFinalSubmitClick && isLastSingleStep && state.selectedOption !== null;

    if (state.waitingFinalSubmit || shouldFinalizeFromFinalSubmitClick) {
      if (isTest && handleSubmit) {
        handleSubmit();
        return;
      }

      setState((prev) => ({
        ...prev,
        phase: 'results',
        finished: true,
        selectedOption: null,
        waitingFinalSubmit: false,
        reviewIndex: Math.max(prev.userAnswers.length - 1, 0),
      }));
      return;
    }

    if (!state.selectedOption || !currentStep) return;
    if (isTest) {
      if (handleSubmit && state.currentSingleIndex === totalSingleSteps - 1) {
        handleSubmit();
        return;
      }

      if (state.currentSingleIndex < totalSingleSteps - 1) {
        setState((prev) => ({
          ...prev,
          currentSingleIndex: prev.currentSingleIndex + 1,
          selectedOption: null,
          waitingFinalSubmit: false,
        }));
      }
      return;
    }

    if (handleSubmit && state.currentSingleIndex === totalSingleSteps - 1) {
      handleSubmit();
      return;
    }

    const correct = isAnswerCorrect(currentStep.answers, state.selectedOption);
    const newAnswer = createUserStepAnswer(
      state.currentSingleIndex,
      'single',
      currentStep.statement || '',
      state.selectedOption,
      correct,
      currentStep.answers || [],
    );

    const updatedAnswers = [...state.userAnswers, newAnswer];
    setState((prev) => ({ ...prev, userAnswers: updatedAnswers }));
    setUserAnswersAnswered?.(updatedAnswers);

    if (state.currentSingleIndex < totalSingleSteps - 1) {
      setState((prev) => ({
        ...prev,
        currentSingleIndex: prev.currentSingleIndex + 1,
        selectedOption: null,
        waitingFinalSubmit: false,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        phase: 'results',
        finished: true,
        reviewIndex: totalMultipleSteps + totalSingleSteps - 1,
        waitingFinalSubmit: false,
      }));
    }
  }, [
    state.selectedOption,
    state.currentSingleIndex,
    state.userAnswers,
    state.waitingFinalSubmit,
    currentStep,
    totalSingleSteps,
    handleSubmit,
    setUserAnswersAnswered,
    isTest,
    totalMultipleSteps,
    requireFinalSubmitClick,
  ]);

  const handleNextReviewStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      reviewIndex: Math.min(prev.reviewIndex + 1, prev.userAnswers.length - 1),
    }));
  }, []);

  const handlePrevReviewStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      reviewIndex: Math.max(prev.reviewIndex - 1, 0),
    }));
  }, []);

  const handleSelectCurrentStep = useCallback(
    (stepIndex: number, stepType: string) => {
      const existingAnswer = state.userAnswers.find(
        (a) => a.stepIndex === stepIndex && a.stepType === stepType,
      );

      if (stepType === 'multiple') {
        setState((prev) => ({
          ...prev,
          currentMultipleIndex: stepIndex,
          phase: 'multiple',
          selectedOption: existingAnswer?.selectedAnswerId || null,
          waitingFinalSubmit: false,
        }));
      } else if (stepType === 'single') {
        setState((prev) => ({
          ...prev,
          currentSingleIndex: stepIndex,
          phase: 'single',
          selectedOption: existingAnswer?.selectedAnswerId || null,
          waitingFinalSubmit: false,
        }));
      }
    },
    [state.userAnswers],
  );

  return {
    state,
    currentStep,
    totalMultipleSteps,
    totalSingleSteps,
    handleMultipleOptionClick,
    handleSingleOptionClick,
    handleSingleSubmit,
    handleNextReviewStep,
    handlePrevReviewStep,
    handleSelectCurrentStep,
  };
};
